import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';
import { X, ChevronDown, ChevronRight, MapPin, Navigation, Loader2, LocateFixed, Search } from 'lucide-react';
import { CATEGORY_ATTRIBUTES, getCategoryAttrKey } from '../../utils/categoryAttributes';
import { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents, Circle } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { showToast } from '../../Layout';

// Fix for default marker icons in Leaflet with React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

function MapPicker({ onPick, position }) {
    const map = useMapEvents({
        click(e) {
            onPick(e.latlng.lat, e.latlng.lng);
        },
    });

    useEffect(() => {
        if (position) {
            map.flyTo(position, map.getZoom());
        }
    }, [position, map]);

    return position ? <Marker position={position} /> : null;
}

export default function FilterSidebar({ filters, onFilterChange, isOpen, onClose, categoryName }) {
  const { t, language } = useLanguage();
  const { theme } = useTheme();
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [isSearchingLocation, setIsSearchingLocation] = useState(false);

  const categoryAttrKey = getCategoryAttrKey(categoryName);
  const attrFields = categoryAttrKey ? CATEGORY_ATTRIBUTES[categoryAttrKey].fields : [];

  const handleLocationSearch = async (e) => {
    if (e) e.preventDefault();
    const val = filters.location;
    if (!val || val.length < 2) return;

    setIsSearchingLocation(true);
    try {
        // Search specifically in Israel for better accuracy
        const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(val + ', Israel')}&limit=1`);
        const data = await res.json();
        if (data && data[0]) {
            onFilterChange({ 
                ...filters, 
                latitude: parseFloat(data[0].lat), 
                longitude: parseFloat(data[0].lon),
                useProximity: true
            });
            showToast(t('locationFound', 'Location found on map!'), 'success');
        } else {
            // Try without ", Israel" just in case
            const res2 = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(val)}&limit=1`);
            const data2 = await res2.json();
            if (data2 && data2[0]) {
                onFilterChange({ 
                    ...filters, 
                    latitude: parseFloat(data2[0].lat), 
                    longitude: parseFloat(data2[0].lon),
                    useProximity: true
                });
                showToast(t('locationFound', 'Location found on map!'), 'success');
            } else {
                showToast(t('locationNotFound', 'Could not find that location'), 'error');
            }
        }
    } catch (err) {
        console.error('Geocoding error:', err);
        showToast(t('searchError', 'Error searching for location'), 'error');
    } finally {
        setIsSearchingLocation(false);
    }
  };

  const handleSliderChange = (e) => {
    onFilterChange({ ...filters, priceRange: [filters.priceRange[0], parseInt(e.target.value)] });
  };

  const handleRadiusChange = (e) => {
    onFilterChange({ ...filters, radius: parseInt(e.target.value) });
  };

  const handleProximityToggle = () => {
    const newState = !filters.useProximity;
    
    if (newState && !filters.latitude) {
      getCurrentLocation();
      // Don't toggle yet, wait for location
    } else {
      onFilterChange({ ...filters, useProximity: newState });
    }
  };

  const getCurrentLocation = () => {
    if ("geolocation" in navigator) {
      setIsGettingLocation(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          onFilterChange({ 
            ...filters, 
            latitude: position.coords.latitude, 
            longitude: position.coords.longitude,
            useProximity: true,
            location: '' // Clear text location when using coordinates
          });
          setIsGettingLocation(false);
          showToast(t('locationCaptured', 'Location captured!'), 'success');
        },
        (error) => {
          console.error("Error getting location:", error);
          setIsGettingLocation(false);
          let errorMsg = t('locationError', 'Could not get your location');
          if (error.code === 1) errorMsg = t('locationDenied', 'Location permission denied');
          showToast(errorMsg, 'error');
          onFilterChange({ ...filters, useProximity: false });
        },
        { timeout: 10000, enableHighAccuracy: true }
      );
    } else {
        showToast(t('geolocationNotSupported', 'Geolocation is not supported by your browser'), 'error');
    }
  };
  
  const handleCheckboxChange = (group, value) => {
    const currentValues = filters[group] || [];
    const newValues = currentValues.includes(value)
      ? currentValues.filter(v => v !== value)
      : [...currentValues, value];
    onFilterChange({ ...filters, [group]: newValues });
  };

  const handleAttributeChange = (name, value) => {
    const currentAttrs = filters.attributes || {};
    onFilterChange({ 
        ...filters, 
        attributes: { ...currentAttrs, [name]: value } 
    });
  };

  const conditionOptions = ['new', 'like_new', 'excellent', 'good', 'fair'];
  const cashOptions = ['can_add', 'can_receive', 'can_add_or_receive', 'prefer_exchange'];

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}
      
      {/* Sidebar */}
      <aside className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-background border-r p-4 
        transform transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0 lg:static lg:z-0 lg:h-auto lg:sticky lg:top-24 lg:border-r
        lg:overflow-visible overflow-y-auto
      `}>
        <div className="flex justify-between items-center lg:hidden mb-4">
          <h3 className="text-lg font-semibold">{t('filters', 'Filters')}</h3>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded-full"><X size={20} /></button>
        </div>

        <div className="space-y-4">
        {/* Dynamic Category Filters */}
        {attrFields.length > 0 && (
            <div className="space-y-4 pb-6 border-b border-border">
                <h4 className="font-black text-xs uppercase tracking-widest text-primary flex items-center gap-2">
                    {categoryName} {t('specificFilters', 'Filters')}
                </h4>
                {attrFields.map(field => {
                    if (field.type === 'select') {
                        return (
                            <div key={field.name}>
                                <label className="block text-xs font-bold mb-2 uppercase text-muted-foreground">
                                    {language === 'he' ? field.label_he : field.label_en}
                                </label>
                                <select 
                                    className="w-full bg-muted border-none rounded-lg p-2 text-sm focus:ring-2 ring-primary/20 outline-none"
                                    value={filters.attributes?.[field.name] || ''}
                                    onChange={(e) => handleAttributeChange(field.name, e.target.value)}
                                >
                                    <option value="">{t('all', 'All')}</option>
                                    {field.options.map(opt => (
                                        <option key={opt} value={opt}>{opt}</option>
                                    ))}
                                </select>
                            </div>
                        );
                    }
                    if (field.type === 'boolean') {
                        return (
                            <label key={field.name} className="flex items-center justify-between group cursor-pointer">
                                <span className="text-sm font-bold group-hover:text-primary transition-colors">
                                    {language === 'he' ? field.label_he : field.label_en}
                                </span>
                                <input 
                                    type="checkbox"
                                    checked={!!filters.attributes?.[field.name]}
                                    onChange={(e) => handleAttributeChange(field.name, e.target.checked)}
                                    className="form-checkbox h-4 w-4 text-primary rounded border-muted bg-muted"
                                />
                            </label>
                        );
                    }
                    return null;
                })}
            </div>
        )}

        <div>
          <h4 className="font-semibold mb-2">{t('priceRange')}</h4>
          <input 
            type="range" 
            min="0" 
            max="1000000" 
            value={filters.priceRange[1]} 
            onChange={handleSliderChange}
            className="w-full"
          />
          <div className="text-sm text-muted-foreground mt-1">{t('upTo')} ${filters.priceRange[1].toLocaleString()}</div>
        </div>

        {/* Condition Side-to-Side Selector */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-bold text-xs uppercase tracking-widest text-muted-foreground">{t('condition', 'Condition')}</h4>
            {filters.conditions.length > 0 && (
              <button 
                type="button"
                onClick={() => onFilterChange({ ...filters, conditions: [] })}
                className="text-[10px] font-black text-primary uppercase tracking-widest hover:underline"
              >
                {t('clear', 'Clear')}
              </button>
            )}
          </div>
          <div className="flex items-center gap-1.5 overflow-x-auto pb-2 scrollbar-none snap-x touch-pan-x">
            <button
              type="button"
              onClick={() => onFilterChange({ ...filters, conditions: [] })}
              className={`px-3.5 py-2 rounded-xl text-xs font-black whitespace-nowrap transition-all shrink-0 snap-start border ${
                filters.conditions.length === 0
                  ? 'bg-primary text-primary-content border-primary shadow-sm scale-105'
                  : 'bg-muted/40 hover:bg-muted text-muted-foreground border-border/40'
              }`}
            >
              {t('all', 'All')}
            </button>
            {conditionOptions.map(opt => {
              const isSelected = filters.conditions.includes(opt);
              return (
                <button
                  key={opt}
                  type="button"
                  onClick={() => handleCheckboxChange('conditions', opt)}
                  className={`px-3.5 py-2 rounded-xl text-xs font-black whitespace-nowrap transition-all shrink-0 snap-start border ${
                    isSelected
                      ? 'bg-primary text-primary-content border-primary shadow-sm scale-105'
                      : 'bg-muted/40 hover:bg-muted text-muted-foreground border-border/40'
                  }`}
                >
                  {t(opt)}
                </button>
              );
            })}
          </div>
        </div>
        
        {/* Cash Flexibility Side-to-Side Selector */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-bold text-xs uppercase tracking-widest text-muted-foreground">{t('cashFlexibility', 'Cash Options')}</h4>
            {filters.cashOptions.length > 0 && (
              <button 
                type="button"
                onClick={() => onFilterChange({ ...filters, cashOptions: [] })}
                className="text-[10px] font-black text-primary uppercase tracking-widest hover:underline"
              >
                {t('clear', 'Clear')}
              </button>
            )}
          </div>
          <div className="flex items-center gap-1.5 overflow-x-auto pb-2 scrollbar-none snap-x touch-pan-x">
            <button
              type="button"
              onClick={() => onFilterChange({ ...filters, cashOptions: [] })}
              className={`px-3.5 py-2 rounded-xl text-xs font-black whitespace-nowrap transition-all shrink-0 snap-start border ${
                filters.cashOptions.length === 0
                  ? 'bg-emerald-500 text-white border-emerald-500 shadow-sm scale-105'
                  : 'bg-muted/40 hover:bg-muted text-muted-foreground border-border/40'
              }`}
            >
              {t('all', 'All')}
            </button>
            {cashOptions.map(opt => {
              const isSelected = filters.cashOptions.includes(opt);
              return (
                <button
                  key={opt}
                  type="button"
                  onClick={() => handleCheckboxChange('cashOptions', opt)}
                  className={`px-3.5 py-2 rounded-xl text-xs font-black whitespace-nowrap transition-all shrink-0 snap-start border ${
                    isSelected
                      ? 'bg-emerald-500 text-white border-emerald-500 shadow-sm scale-105'
                      : 'bg-muted/40 hover:bg-muted text-muted-foreground border-border/40'
                  }`}
                >
                  {t(opt)}
                </button>
              );
            })}
          </div>
        </div>

        <div>
           <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold">{t('location')}</h4>
                {(filters.latitude || filters.longitude || filters.location) && (
                    <button 
                        onClick={() => onFilterChange({ ...filters, location: '', latitude: null, longitude: null, useProximity: false })}
                        className="text-[10px] font-black text-primary uppercase tracking-widest hover:underline"
                    >
                        {t('clear', 'Clear')}
                    </button>
                )}
           </div>
           <div className="flex gap-2 mb-4">
                <div className="relative flex-1 group">
                    <input 
                        type="text"
                        placeholder={t('locationPlaceholder')}
                        value={filters.location}
                        onChange={(e) => onFilterChange({ ...filters, location: e.target.value })}
                        onKeyDown={(e) => e.key === 'Enter' && handleLocationSearch()}
                        className="w-full bg-muted border-none p-2 pr-10 rounded-lg text-sm focus:ring-1 focus:ring-primary transition-all"
                    />
                    <button 
                        onClick={handleLocationSearch}
                        disabled={isSearchingLocation}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary transition-colors disabled:opacity-50"
                    >
                        {isSearchingLocation ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
                    </button>
                </div>
           </div>

            <div className="mb-4 space-y-2 relative z-0">
                <div className="h-64 w-full rounded-xl overflow-hidden border border-border shadow-inner">
                    <MapContainer 
                        center={filters.latitude && filters.longitude ? [filters.latitude, filters.longitude] : [31.5, 34.75]} 
                        zoom={filters.latitude ? 12 : 7} 
                        minZoom={6}
                        maxBounds={[
                            [29.3, 33.5], // South West
                            [33.5, 36.5]  // North East
                        ]}
                        maxBoundsViscosity={1.0}
                        style={{ height: '100%', width: '100%' }}
                        zoomControl={true}
                    >
                        <TileLayer
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://www.esri.com/">Esri</a>'
                            url={theme === 'dark' 
                                ? "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}"
                                : "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            }
                        />
                        {filters.latitude && filters.longitude && filters.useProximity && (
                            <Circle 
                                center={[filters.latitude, filters.longitude]}
                                radius={(filters.radius || 50) * 1000}
                                pathOptions={{
                                    fillColor: '#3b82f6',
                                    color: '#3b82f6',
                                    fillOpacity: 0.15,
                                    weight: 2,
                                    dashArray: '5, 10'
                                }}
                            />
                        )}
                        <MapPicker 
                            position={filters.latitude && filters.longitude ? [filters.latitude, filters.longitude] : null}
                            onPick={(lat, lng) => onFilterChange({ ...filters, latitude: lat, longitude: lng, useProximity: true, location: '' })}
                        />
                    </MapContainer>
                </div>
                <p className="text-[9px] font-bold text-muted-foreground text-center italic">{t('clickMapToSelect', 'Click map to select location')}</p>
            </div>

            <div className="bg-primary/5 rounded-2xl p-4 border border-primary/10 space-y-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <MapPin size={16} className="text-primary" />
                        <span className="text-xs font-black uppercase tracking-widest">{t('nearbySearch', 'Nearby Search')}</span>
                    </div>
                    <button 
                        onClick={handleProximityToggle}
                        className={`w-10 h-5 rounded-full transition-all relative ${filters.useProximity ? 'bg-primary' : 'bg-muted'}`}
                    >
                        <div className={`absolute top-1 w-3 h-3 bg-white rounded-full transition-all ${filters.useProximity ? 'right-1' : 'left-1'}`} />
                    </button>
                </div>

                {filters.useProximity && (
                    <div className="space-y-3 animate-in fade-in slide-in-from-top-2 duration-300">
                        <div className="flex justify-between items-center text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                            <span>{t('radius', 'Radius')}</span>
                            <span className="text-primary">{filters.radius || 50} km</span>
                        </div>
                        <input 
                            type="range"
                            min="1"
                            max="500"
                            value={filters.radius || 50}
                            onChange={handleRadiusChange}
                            className="w-full accent-primary h-1.5 rounded-full"
                        />
                        <button 
                            onClick={getCurrentLocation}
                            disabled={isGettingLocation}
                            className="w-full py-2 bg-background border border-border rounded-xl text-[9px] font-black uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-muted transition-all"
                        >
                            {isGettingLocation ? <Loader2 size={12} className="animate-spin" /> : <Navigation size={12} />}
                            {t('updateLocation', 'Update Location')}
                        </button>
                    </div>
                )}
            </div>
        </div>

        <button 
          onClick={() => onFilterChange({ priceRange: [0, 1000000], conditions: [], cashOptions: [], location: '', useProximity: false, radius: 50, latitude: null, longitude: null })}
          className="w-full py-2 bg-secondary text-secondary-content rounded-md"
        >
          {t('clearAll')}
        </button>
      </div>
    </aside>
    </>
  );
}
